import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Brain,
  Palette,
  Plug,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ListNavGroup } from "@/components/ListNav";
import { type CapabilityRequirement, hasCapability } from "@/features/account/RequireCapability";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { useViewport } from "@/responsive/viewport";

export const SETTINGS_SECTION_CAPABILITY = {
  memory: ["knowledgeExtractionEnabled", "agentMemoryEnabled"],
  authorizations: "automatedSyncEnabled",
  rally: "rallyEnabled",
} as const satisfies Record<string, CapabilityRequirement>;

export type GatedSettingsSection = keyof typeof SETTINGS_SECTION_CAPABILITY;

export function useSettingsNav(): ListNavGroup[] {
  const { t } = useTranslation("settings");
  const caps = useCapabilities();
  const { isMobile } = useViewport();
  const off = (section: GatedSettingsSection) => !hasCapability(caps, SETTINGS_SECTION_CAPABILITY[section]);
  return [
    {
      id: "general",
      label: t("nav.groups.general"),
      items: [
        { to: "/settings", label: t("nav.profile"), icon: <UserRound />, end: true, hidden: isMobile },
        { to: "/settings/appearance", label: t("nav.appearance"), icon: <Palette /> },
        { to: "/settings/ai", label: t("nav.ai"), icon: <Sparkles /> },
        { to: "/settings/memory", label: t("nav.memory"), icon: <Brain />, hidden: off("memory") },
        { to: "/settings/notifications", label: t("nav.notifications"), icon: <Bell /> },
      ],
    },
    {
      id: "access",
      label: t("nav.groups.access"),
      items: [
        { to: "/settings/security", label: t("nav.security"), icon: <ShieldCheck /> },
        { to: "/settings/connections", label: t("nav.connections"), icon: <Plug /> },
        {
          to: "/settings/authorizations",
          label: t("nav.authorizations"),
          icon: <Activity />,
          hidden: off("authorizations"),
        },
        { to: "/settings/rally", label: t("nav.rally"), icon: <Bot />, hidden: off("rally") },
      ],
    },
    {
      id: "activity",
      label: t("nav.groups.activity"),
      items: [
        { to: "/settings/usage", label: t("nav.usage"), icon: <BarChart3 /> },
        { to: "/settings/teams", label: t("nav.teams"), icon: <Users /> },
      ],
    },
  ];
}
